import * as React from 'react'

import { EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC } from '../../utils/constants'

const closeIconSrc = `${SMALL_ICON_BASE_SRC}/close.svg`
const chevronIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/chevron-right.svg`

export const STANDARD = 'standard'
export const TEACHER_PREMIUM = 'teacherPremium'
export const SCHOOL_DISTRICT_PREMIUM = 'schoolDistrictPremium'
export const URGENT = 'urgent'

const VARIANT_CLASSES = {
  [STANDARD]: 'standard',
  [TEACHER_PREMIUM]: 'teacher-premium',
  [SCHOOL_DISTRICT_PREMIUM]: 'school-district-premium',
  [URGENT]: 'urgent',
}

const DEFAULT_BUTTON_CLASSNAMES = {
  [STANDARD]: 'quill-button outlined small grey focus-on-light',
  [TEACHER_PREMIUM]: 'quill-button outlined small gold focus-on-light',
  [SCHOOL_DISTRICT_PREMIUM]: 'quill-button outlined small maroon focus-on-light',
  [URGENT]: 'quill-button outlined small grey focus-on-dark',
}

interface BannerButton {
  text: string,
  onClick?: () => void,
  href?: string,
  target?: string,
  className?: string,
}

interface PreNavigationBannerProps {
  variant?: string,
  text: string | JSX.Element,
  onDismiss?: () => void,
  dismissAriaLabel?: string,
  button?: BannerButton,
  expandedContent?: React.ReactNode,
  defaultExpanded?: boolean,
  expandAriaLabel?: string,
}

export const PreNavigationBanner = ({
  variant = STANDARD,
  text,
  onDismiss,
  dismissAriaLabel = 'Dismiss banner',
  button,
  expandedContent,
  defaultExpanded = false,
  expandAriaLabel = 'Toggle banner details',
}: PreNavigationBannerProps) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded)

  const isExpandable = !!expandedContent
  const isDismissable = !isExpandable && !button && !!onDismiss
  const focusClass = variant === URGENT ? 'focus-on-dark' : 'focus-on-light'
  const controlClass = isExpandable ? 'expandable' : (button ? 'has-button' : 'dismissable')
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES[STANDARD]

  function toggleExpanded() { setIsExpanded(previous => !previous) }

  function renderButton() {
    if (!button) { return null }

    const { text: buttonText, onClick, href, target, className } = button
    const buttonClassName = className || DEFAULT_BUTTON_CLASSNAMES[variant] || DEFAULT_BUTTON_CLASSNAMES[STANDARD]

    if (href) {
      return <a className={buttonClassName} href={href} rel="noopener noreferrer" target={target}>{buttonText}</a>
    }
    return <button className={buttonClassName} onClick={onClick} type="button">{buttonText}</button>
  }

  return (
    <section className={`pre-navigation-banner ${variantClass} ${controlClass}`}>
      <div className="pre-navigation-banner__main">
        {isExpandable && (
          <button aria-expanded={isExpanded} aria-label={expandAriaLabel} className={`pre-navigation-banner__expand-toggle ${focusClass}`} onClick={toggleExpanded} type="button">
            <img alt="" className={`pre-navigation-banner__chevron ${isExpanded ? 'is-expanded' : ''}`} src={chevronIconSrc} />
          </button>
        )}
        <div className="pre-navigation-banner__text">{text}</div>
        {renderButton()}
        {isDismissable && (
          <button aria-label={dismissAriaLabel} className={`pre-navigation-banner__close ${focusClass}`} onClick={onDismiss} type="button">
            <img alt="" className="pre-navigation-banner__close-icon" src={closeIconSrc} />
          </button>
        )}
      </div>
      {isExpandable && isExpanded && (
        <div className="pre-navigation-banner__expanded">{expandedContent}</div>
      )}
    </section>
  )
}

export default PreNavigationBanner
